import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { googleLoginUrl } from '../lib/api'

const RESET_URL = 'https://accminecraft.net/password/reset'

function AuthShell({ lead, children }: { lead: string; children: ReactNode }) {
  return (
    <div className="auth-screen">
      <div className="auth-box">
        <div className="auth-brand">
          <Link to="/" aria-label="Trang chủ">
            <img src="/image/logo2.png" alt="Accminecraft" />
          </Link>
          <p>{lead}</p>
        </div>
        {children}
      </div>
    </div>
  )
}

function SocialLogin() {
  return (
    <>
      <div className="auth-divider">
        <span>HOẶC</span>
      </div>
      <div className="auth-social">
        <button type="button" className="auth-social-btn auth-facebook" disabled>
          <i className="fa-brands fa-facebook" aria-hidden /> Facebook (bảo trì)
        </button>
        <a className="auth-social-btn auth-google" href={googleLoginUrl('/account')}>
          <i className="fa-brands fa-google" aria-hidden /> Google
        </a>
      </div>
    </>
  )
}

export function LoginPage() {
  const { login } = useAuth()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(
    params.get('error') === 'google' ? 'Đăng nhập Google không thành công.' : '',
  )

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr('')
    setBusy(true)
    try {
      await login(email, password, remember)
      nav('/account')
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Lỗi')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell lead="Một ngày là bạn mãi mãi là bạn">
      <form className="auth-form" onSubmit={onSubmit}>
        <label className="auth-field">
          <span>Nhập email</span>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="Địa chỉ email của bạn"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span>Nhập mật khẩu</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            placeholder="Mật khẩu"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <a className="auth-forgot" href={RESET_URL} target="_blank" rel="noreferrer">
          quên mật khẩu?
        </a>
        <label className="auth-check">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          Ghi nhớ thiết bị
        </label>
        {err ? <p className="auth-error">{err}</p> : null}
        <button className="auth-btn auth-btn-dark" type="submit" disabled={busy}>
          {busy ? 'ĐANG ĐĂNG NHẬP…' : 'ĐĂNG NHẬP'}
        </button>
        <Link className="auth-btn auth-btn-purple" to="/register">
          TẠO TÀI KHOẢN
        </Link>
      </form>
      <SocialLogin />
    </AuthShell>
  )
}

export function RegisterPage() {
  const { register } = useAuth()
  const nav = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agree, setAgree] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr('')
    setBusy(true)
    try {
      await register(name, email, password)
      nav('/account')
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Lỗi')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell lead="Hi bro ! Hãy tạo tài khoản và chính thức trở thành bạn bè của chúng tôi.">
      <form className="auth-form" onSubmit={onSubmit}>
        <label className="auth-field">
          <span>Tên của bạn</span>
          <input
            required
            autoComplete="nickname"
            placeholder="Tên của bạn là gì?"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span>Nhập email</span>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="Địa chỉ email của bạn"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span>Mật khẩu</span>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="Tạo một mật khẩu đủ mạnh"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className="auth-check">
          <input type="checkbox" required checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          Đồng ý với thỏa thuận dịch vụ
        </label>
        {err ? <p className="auth-error">{err}</p> : null}
        <button className="auth-btn auth-btn-purple" type="submit" disabled={busy}>
          {busy ? 'ĐANG TẠO…' : 'TẠO TÀI KHOẢN'}
        </button>
        <Link className="auth-btn auth-btn-dark" to="/login">
          ĐĂNG NHẬP
        </Link>
      </form>
      <SocialLogin />
    </AuthShell>
  )
}

export function GoogleDonePage() {
  const { loginWithToken } = useAuth()
  const nav = useNavigate()
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    const hash = new URLSearchParams(window.location.hash.slice(1))
    const token = hash.get('token')
    const next = hash.get('next') || '/account'
    window.history.replaceState(null, '', window.location.pathname)
    if (!token) {
      nav('/login?error=google', { replace: true })
      return
    }
    loginWithToken(token)
      .then(() => nav(next.startsWith('/') && !next.startsWith('//') ? next : '/account', { replace: true }))
      .catch(() => nav('/login?error=google', { replace: true }))
  }, [loginWithToken, nav])

  return (
    <AuthShell lead="Đang đăng nhập…">
      <p className="auth-wait">
        <i className="fa-solid fa-spinner fa-spin" aria-hidden />
      </p>
    </AuthShell>
  )
}
